import React, { useEffect } from 'react';
import { MemoryRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { SitesPage } from './pages/SitesPage';
import { SiteDetailPage } from './pages/SiteDetailPage';
import { ArchivedSitesPage } from './pages/ArchivedSitesPage';
import { WorkersPage } from './pages/WorkersPage';
import { WorkerDetailPage } from './pages/WorkerDetailPage';
import { WorkerTrainingPage } from './pages/WorkerTrainingPage';
import { InductionsPage } from './pages/InductionsPage';
import { InductionDetailPage } from './pages/InductionDetailPage';
import { WasteTrackingPage } from './pages/WasteTrackingPage';
import { WasteDisposalFacilitiesPage } from './pages/WasteDisposalFacilitiesPage';
import { SWMSPage } from './pages/SWMSPage';
import { TimesheetsPage } from './pages/TimesheetsPage';
import { HealthMonitoringPage } from './pages/HealthMonitoringPage';
import { VehiclesPage } from './pages/VehiclesPage';
import { VehiclePlantServicingPage } from './pages/VehiclePlantServicingPage';
import { PreStartReportsPage } from './pages/PreStartReportsPage';
import { OutOfAreaRequestsPage } from './pages/OutOfAreaRequestsPage';
import { ReportsPage } from './pages/ReportsPage';
import { UserManagementPage } from './pages/UserManagementPage';
import { SettingsPage } from './pages/SettingsPage';
import { ProfilePage } from './pages/ProfilePage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import SafetyPaperworkPage from './pages/SafetyPaperworkPage';
import { CreditCardExpensesPage } from './pages/CreditCardExpensesPage';
import { SWMSTabPage } from './pages/SWMSTabPage';
import { SitesManagementPage } from './pages/SitesManagementPage';
import { OperationsPage } from './pages/OperationsPage';
import { WorkforcePage } from './pages/WorkforcePage';
import { FleetPage } from './pages/FleetPage';
import { LiveBoardPage } from './pages/LiveBoardPage';

import { Layout } from './components/layout/Layout';

interface SafetyPortalAppProps {
  supabaseUrl: string;
  supabaseAnonKey: string;
  className?: string;
  style?: React.CSSProperties;
  useExternalRouter?: boolean; // New prop to control router usage
}

// Internal routing component for when using external router
const SafetyPortalRoutes: React.FC = () => {
  return (
    <AuthProvider loginPath="/safety-portal/login">
      <Routes>
        <Route path="/safety-portal/login" element={<LoginPage />} />
        <Route path="/safety-portal/*" element={
          <ProtectedRoute>
            <Layout>
              <Routes>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/live-board" element={<LiveBoardPage />} />
                <Route path="/sites" element={<SitesPage />} />
                <Route path="/sites/:id" element={<SiteDetailPage />} />
                <Route path="/archived-sites" element={<ArchivedSitesPage />} />
                <Route path="/workers" element={<WorkersPage />} />
                <Route path="/workers/training" element={<WorkerTrainingPage />} />
                <Route path="/workers/:id" element={<WorkerDetailPage />} />
                <Route path="/inductions" element={<InductionsPage />} />
                <Route path="/inductions/:id" element={<InductionDetailPage />} />
                <Route path="/waste-tracking" element={<WasteTrackingPage />} />
                <Route path="/waste-disposal-facilities" element={<WasteDisposalFacilitiesPage />} />
                <Route path="/timesheets" element={<TimesheetsPage />} />
                <Route path="/health-monitoring" element={<HealthMonitoringPage />} />
                <Route path="/vehicles" element={<VehiclesPage />} />
                <Route path="/safety-paperwork" element={<SafetyPaperworkPage />} />
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/credit-card-expenses" element={<CreditCardExpensesPage />} />
                <Route path="/users" element={<UserManagementPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        } />
      </Routes>
    </AuthProvider>
  );
};

// Standalone routing component for when using internal router
const SafetyPortalStandalone: React.FC = () => {
  return (
    <Router>
      <AuthProvider loginPath="/login">
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/*" element={
            <ProtectedRoute>
              <Layout>
                <Routes>
                  <Route path="/" element={<DashboardPage />} />
                  <Route path="/operations" element={<OperationsPage />} />
                  <Route path="/live-board" element={<LiveBoardPage />} />
                  <Route path="/sites-management" element={<SitesManagementPage />} />
                  <Route path="/sites" element={<SitesPage />} />
                  <Route path="/sites/:id" element={<SiteDetailPage />} />
                  <Route path="/archived-sites" element={<ArchivedSitesPage />} />
                  <Route path="/swms" element={<SWMSTabPage />} />
                  <Route path="/workforce" element={<WorkforcePage />} />
                  <Route path="/workers" element={<WorkersPage />} />
                  <Route path="/workers/training" element={<WorkerTrainingPage />} />
                  <Route path="/workers/:id" element={<WorkerDetailPage />} />
                  <Route path="/inductions" element={<InductionsPage />} />
                  <Route path="/inductions/:id" element={<InductionDetailPage />} />
                  <Route path="/waste-tracking" element={<WasteTrackingPage />} />
                  <Route path="/waste-disposal-facilities" element={<WasteDisposalFacilitiesPage />} />
                  <Route path="/timesheets" element={<TimesheetsPage />} />
                  <Route path="/reports" element={<ReportsPage />} />
                  <Route path="/health-monitoring" element={<HealthMonitoringPage />} />
                  <Route path="/fleet" element={<FleetPage />} />
                  <Route path="/vehicles" element={<VehiclesPage />} />
                  <Route path="/vehicle-plant-servicing" element={<VehiclePlantServicingPage />} />
                  <Route path="/pre-start-reports" element={<PreStartReportsPage />} />
                  <Route path="/safety-paperwork" element={<SafetyPaperworkPage />} />
                  <Route path="/out-of-area-requests" element={<OutOfAreaRequestsPage />} />
                  <Route path="/credit-card-expenses" element={<CreditCardExpensesPage />} />
                  <Route path="/users" element={<UserManagementPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/privacy" element={<PrivacyPolicyPage />} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          } />
        </Routes>
      </AuthProvider>
    </Router>
  );
};

export const SafetyPortalApp: React.FC<SafetyPortalAppProps> = ({ 
  supabaseUrl, 
  supabaseAnonKey, 
  className,
  style,
  useExternalRouter = false
}) => {
  return (
    <div className={className} style={style}>
      {useExternalRouter ? <SafetyPortalRoutes /> : <SafetyPortalStandalone />}
    </div>
  );
};

export default SafetyPortalApp;